import { reportBreadcrumb, reportException } from './errorReporting'

/**
 * Shared recovery path for stale/poisoned lazy chunk loads.
 *
 * Background (Sentry JAVASCRIPT-REACT-7): a plain window.location.reload()
 * does NOT bypass the service worker. When Cache Storage holds a bad body
 * under a hashed asset URL, every reload re-serves that same body, so the
 * old one-shot reload could never recover and the user was trapped on the
 * error boundary. Recovery therefore has to purge Cache Storage and refresh
 * the service worker registration BEFORE reloading.
 */

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk \d+ failed/,
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /error loading dynamically imported module/i,
  /'text\/html'.*JavaScript MIME type/,
]

const ATTEMPT_KEY = 'chunkRecoveryAttempts'
const MAX_ATTEMPTS = 2
const ATTEMPT_WINDOW_MS = 60_000

interface AttemptRecord {
  count: number
  firstAt: number
}

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message))
}

function readAttempts(): AttemptRecord {
  const empty: AttemptRecord = { count: 0, firstAt: 0 }
  if (typeof window === 'undefined') return empty
  try {
    const raw = window.sessionStorage.getItem(ATTEMPT_KEY)
    if (!raw) return empty
    const parsed = JSON.parse(raw) as Partial<AttemptRecord>
    if (typeof parsed.count !== 'number' || typeof parsed.firstAt !== 'number') {
      return empty
    }
    // Attempts age out, so a failure hours later starts with a fresh budget
    // instead of inheriting an exhausted one.
    if (Date.now() - parsed.firstAt > ATTEMPT_WINDOW_MS) return empty
    return { count: parsed.count, firstAt: parsed.firstAt }
  } catch {
    return empty
  }
}

function recordAttempt(): number {
  const current = readAttempts()
  const next: AttemptRecord = {
    count: current.count + 1,
    firstAt: current.firstAt || Date.now(),
  }
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(next))
  } catch {
    // Private mode / storage disabled. Fall through; the cap simply becomes
    // best-effort rather than guaranteed.
  }
  return next.count
}

export function clearRecoveryAttempts(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ATTEMPT_KEY)
  } catch {
    // Nothing to do.
  }
}

/**
 * Deletes every Cache Storage bucket and asks the active service worker to
 * do the same, then refreshes its registration. Never rejects — recovery
 * must still reload even if one of these steps is unavailable.
 */
export async function purgeCaches(): Promise<void> {
  const tasks: Array<Promise<unknown>> = []

  if (typeof caches !== 'undefined') {
    tasks.push(
      caches
        .keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => undefined)
    )
  }

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.controller?.postMessage({ type: 'PURGE_CACHES' })
    tasks.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          Promise.all(registrations.map((registration) => registration.update()))
        )
        .catch(() => undefined)
    )
  }

  await Promise.all(tasks)
}

/**
 * Full recovery: purge, then reload. Capped at MAX_ATTEMPTS inside a short
 * window so a genuinely missing chunk degrades to the error boundary rather
 * than an infinite reload loop.
 *
 * Returns false when the attempt budget is spent, which the caller should
 * treat as "give up and show the error".
 */
export async function recoverFromChunkFailure(error: unknown): Promise<boolean> {
  if (typeof window === 'undefined') return false

  const attempt = recordAttempt()
  if (attempt > MAX_ATTEMPTS) {
    reportException(error, {
      tags: {
        component: 'lazyWithRetry',
        persistentFailure: 'true',
        recoveryAttempts: String(attempt - 1),
      },
    })
    return false
  }

  // Breadcrumb only. A recovered load is not an incident, but the count
  // still needs to be visible if this starts firing at scale.
  reportBreadcrumb({
    category: 'chunk-recovery',
    level: 'warning',
    message: `Chunk load failed, purging caches and reloading (attempt ${attempt})`,
  })

  await purgeCaches()
  window.location.reload()
  return true
}
